/**
 * In-app human support conversation UI (gap row 48, I8).
 *
 * Three views: the requester's threads (with unread counts), a new-request form
 * (subject, category, message, optional attachments) and a thread view
 * (messages from the user / Karexia team / system, reply box, resolve). Used by
 * the shell drawer (SupportButton) and the full page (/support?thread=…).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useT } from '@/i18n';
import { confirmAsync } from '@/ui';
import { describeApiError } from '@/lib/api/client';
import { GUIDES_BASE_URL } from '@/lib/moveGuides';
import {
  SUPPORT_MAX_FILES,
  downloadSupportAttachment,
  encodeAttachments,
  supportApi,
  type SupportCategory,
  type SupportThread,
  type SupportThreadDetail,
} from '@/lib/api/support';

const CATEGORIES: SupportCategory[] = ['question', 'bug', 'billing', 'feature', 'account', 'other'];

const STATUS_CLASS: Record<string, string> = {
  open: 'bg-primary/15 text-primary',
  pending: 'bg-warning/15 text-warning',
  resolved: 'bg-success/15 text-success',
  closed: 'bg-surface-2 text-muted',
};

const INPUT =
  'w-full rounded-token border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring';

export function fileErrorMessage(
  err: unknown,
  t: (k: 'support.errFileType' | 'support.errFileSize' | 'support.errFileCount', v?: Record<string, string>) => string,
): string | null {
  if (!(err instanceof Error)) return null;
  const [kind, name = ''] = err.message.split(':');
  if (kind === 'type') return t('support.errFileType', { name });
  if (kind === 'size') return t('support.errFileSize', { name });
  if (kind === 'max_files') return t('support.errFileCount');
  return null;
}

export interface SupportPanelProps {
  initialThreadId?: string | null;
  onUnreadChange?: (n: number) => void;
}

export function SupportPanel({ initialThreadId = null, onUnreadChange }: SupportPanelProps) {
  const { t: tx } = useT();
  const { t, locale } = useT();
  const [view, setView] = useState<'list' | 'new' | 'thread'>(initialThreadId ? 'thread' : 'list');
  const [threads, setThreads] = useState<SupportThread[] | null>(null);
  const [thread, setThread] = useState<SupportThreadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<SupportCategory>('question');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [reply, setReply] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const refreshList = useCallback(async () => {
    try {
      const res = await supportApi.list();
      setThreads(res.items);
      onUnreadChange?.(res.items.reduce((n, x) => n + (x.unread || 0), 0));
    } catch (err) {
      setError(describeApiError(err));
      setThreads([]);
    }
  }, [onUnreadChange]);

  const openThread = useCallback(
    async (id: string) => {
      setError(null);
      try {
        const detail = await supportApi.get(id);
        setThread(detail);
        setView('thread');
        void refreshList();
      } catch (err) {
        setError(describeApiError(err));
      }
    },
    [refreshList],
  );

  useEffect(() => {
    void refreshList();
    if (initialThreadId) void openThread(initialThreadId);
  }, [initialThreadId, openThread, refreshList]);

  const submitNew = async () => {
    setBusy(true);
    setError(null);
    try {
      const attachments = await encodeAttachments(files);
      const created = await supportApi.create({
        subject: subject.trim(),
        category,
        body: body.trim(),
        page_url: typeof window !== 'undefined' ? window.location.pathname : undefined,
        locale,
        attachments,
      });
      setSubject('');
      setBody('');
      setFiles([]);
      setThread(created);
      setView('thread');
      setNotice(t('support.sent'));
      void refreshList();
    } catch (err) {
      setError(fileErrorMessage(err, t) ?? describeApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitReply = async () => {
    if (!thread) return;
    setBusy(true);
    setError(null);
    try {
      const attachments = await encodeAttachments(files);
      setThread(await supportApi.reply(thread.id, reply.trim(), attachments));
      setReply('');
      setFiles([]);
      void refreshList();
    } catch (err) {
      setError(fileErrorMessage(err, t) ?? describeApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    if (!thread) return;
    try {
      setThread(await supportApi.resolve(thread.id));
      void refreshList();
    } catch (err) {
      setError(describeApiError(err));
    }
  };

  const remove = async () => {
    if (!thread) return;
    if (!(await confirmAsync(t('support.deleteConfirm')))) return;
    try {
      await supportApi.remove(thread.id);
      setThread(null);
      setNotice(null);
      setView('list');
      void refreshList();
    } catch (err) {
      setError(describeApiError(err));
    }
  };

  const filePicker = (
    <div>
      <label className="mb-1 block text-xs font-semibold">{t('support.attachments')}</label>
      <input
        ref={fileInput}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv"
        data-testid="support-files"
        onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, SUPPORT_MAX_FILES + 1))}
        className="block w-full text-xs"
      />
      {files.length > 0 && <p className="mt-1 text-xs text-muted">{files.map((f) => f.name).join(', ')}</p>}
    </div>
  );

  return (
    <div className="flex h-full flex-col gap-3 text-sm" data-testid="support-panel">
      {error && (
        <div role="alert" className="rounded-token border border-danger/30 bg-danger/10 px-3 py-2 text-danger">
          {error}
        </div>
      )}
      {notice && view === 'thread' && (
        <div role="status" className="rounded-token border border-success/30 bg-success/10 px-3 py-2 text-success">
          {notice}
        </div>
      )}

      {view === 'list' && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{t('support.myRequests')}</h3>
            <button
              type="button"
              className="rounded-token bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
              onClick={() => {
                setView('new');
                setNotice(null);
              }}
              data-testid="support-new"
            >
              {t('support.newRequest')}
            </button>
          </div>
          {threads === null ? (
            <p className="text-muted">…</p>
          ) : threads.length === 0 ? (
            <p className="text-muted">{t('support.noRequests')}</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="support-thread-list">
              {threads.map((th) => (
                <li key={th.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col items-start gap-1 py-2 text-start hover:bg-surface-2"
                    onClick={() => void openThread(th.id)}
                  >
                    <span className="flex w-full items-center gap-2">
                      <span className="font-medium">{th.subject}</span>
                      {th.unread > 0 && (
                        <span className="rounded-full bg-danger px-1.5 text-[10px] font-bold text-primary-foreground">
                          {t('support.unread', { count: th.unread })}
                        </span>
                      )}
                      <span className={`ms-auto rounded-full px-2 text-[11px] ${STATUS_CLASS[th.status] ?? ''}`}>
                        {t(`support.status_${th.status}` as const)}
                      </span>
                    </span>
                    <span className="line-clamp-1 text-xs text-muted">{th.last_message_preview}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-auto flex flex-wrap gap-3 border-t border-border pt-2 text-xs">
            <a className="text-primary underline" href={`${GUIDES_BASE_URL}/${locale === 'en' ? 'en' : 'fr'}/daily-moves.html`} target="_blank" rel="noreferrer">
              {t('support.guides')}
            </a>
            <a className="text-primary underline" href="https://document.karexia.com" target="_blank" rel="noreferrer">
              {t('support.docs')}
            </a>
          </div>
        </>
      )}

      {view === 'new' && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submitNew();
          }}
          data-testid="support-new-form"
        >
          <button type="button" className="text-xs text-primary" onClick={() => setView('list')}>
            {t('support.back')}
          </button>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold">{t('support.subject')}</span>
            <input name="subject" className={INPUT} value={subject} minLength={3} maxLength={200} required onChange={(e) => setSubject(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold">{t('support.category')}</span>
            <select name="category" className={INPUT} value={category} onChange={(e) => setCategory(e.target.value as SupportCategory)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`support.cat_${c}` as const)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold">{t('support.message')}</span>
            <textarea
              name="body"
              className={INPUT}
              rows={5}
              required
              value={body}
              placeholder={t('support.messagePlaceholder')}
              onChange={(e) => setBody(e.target.value)}
            />
          </label>
          {filePicker}
          <button
            type="submit"
            disabled={busy || subject.trim().length < 3 || !body.trim()}
            className="rounded-token bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            data-testid="support-send"
          >
            {busy ? t('support.sending') : t('support.send')}
          </button>
        </form>
      )}

      {view === 'thread' && thread && (
        <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid="support-thread">
          <button
            type="button"
            className="self-start text-xs text-primary"
            onClick={() => {
              setView('list');
              setNotice(null);
            }}
          >
            {t('support.back')}
          </button>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{thread.subject}</h3>
            <span className={`rounded-full px-2 text-[11px] ${STATUS_CLASS[thread.status] ?? ''}`} data-testid="support-thread-status">
              {t(`support.status_${thread.status}` as const)}
            </span>
            <button
              type="button"
              className="ms-auto text-xs text-danger underline"
              onClick={() => void remove()}
              data-testid="support-delete"
            >
              {t('support.delete')}
            </button>
          </div>
          <ol className="min-h-0 flex-1 space-y-2 overflow-y-auto" data-testid="support-messages">
            {thread.messages.map((m) => (
              <li
                key={m.id}
                className={
                  m.author_type === 'system'
                    ? 'text-center text-[11px] italic text-muted'
                    : `max-w-[90%] rounded-token-lg px-3 py-2 ${
                        m.author_type === 'user' ? 'ms-auto bg-primary/10' : 'bg-surface-2'
                      }`
                }
                data-author={m.author_type}
              >
                {m.author_type !== 'system' && (
                  <div className="mb-0.5 text-[11px] font-semibold text-muted">
                    {m.author_type === 'user' ? t('support.you') : m.author_name || t('support.team')}
                    {m.created_at ? ` · ${new Date(m.created_at).toLocaleString(locale)}` : ''}
                  </div>
                )}
                <p className="whitespace-pre-wrap">{m.body}</p>
                {m.attachments.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="mt-1 block text-xs text-primary underline"
                    onClick={() => void downloadSupportAttachment(a)}
                  >{tx('shell.features.supportSupportPanel.filenameVKb', { filename: a.filename, v: Math.ceil(a.size / 1024) })}</button>
                ))}
              </li>
            ))}
          </ol>
          {thread.status === 'closed' ? (
            <p className="text-xs text-muted">{t('support.closedNote')}</p>
          ) : (
            <form
              className="space-y-2 border-t border-border pt-2"
              onSubmit={(e) => {
                e.preventDefault();
                void submitReply();
              }}
            >
              <textarea
                name="reply"
                className={INPUT}
                rows={3}
                value={reply}
                placeholder={t('support.replyPlaceholder')}
                onChange={(e) => setReply(e.target.value)}
              />
              {filePicker}
              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={busy || !reply.trim()}
                  className="rounded-token bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                  data-testid="support-reply"
                >
                  {busy ? t('support.sending') : t('support.reply')}
                </button>
                {thread.status !== 'resolved' && (
                  <button
                    type="button"
                    className="rounded-token border border-border px-3 py-1.5 text-sm"
                    onClick={() => void resolve()}
                    data-testid="support-resolve"
                  >
                    {t('support.resolve')}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
